// src/pages/ManagerGroupPage.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Switch,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import UnifiedPageWrapper from "../components/Layout/UnifiedPageWrapper";
import SmartTable from "../components/Layout/SmartTable";
import API from "../utils/api";

export default function ManagerGroupPage() {
  const [managers, setManagers] = useState([]);
  const [selectedManager, setSelectedManager] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);

  // 🔹 Fetch all managers
  const fetchManagers = async () => {
    try {
      const res = await API.get("/users/managers/");
      setManagers(res.data);
    } catch (err) {
      console.error("Error fetching managers:", err.response?.data || err.message);
    }
  };

  // 🔹 Save (create or update)
  const handleSaveManager = async () => {
    try {
      if (selectedManager.id) {
        await API.put(`/users/${selectedManager.id}/`, selectedManager);
      } else {
        await API.post("/users/", selectedManager);
      }
      setOpenEdit(false);
      setSelectedManager(null);
      await fetchManagers();
    } catch (err) {
      console.error("Error saving manager:", err.response?.data || err.message);
    }
  };

  // 🔹 Delete
  const handleDeleteManager = async (id) => {
    try {
      await API.delete(`/users/${id}/`);
      await fetchManagers();
    } catch (err) {
      console.error("Error deleting manager:", err);
    }
  };

  // 🔹 Row click opens modal
  const handleRowClick = (params) => {
    setSelectedManager(params.row);
    setOpenEdit(true);
  };

  useEffect(() => {
    fetchManagers();
  }, []);

  // 🔹 Columns
  const columns = [
    { field: "username", headerName: "Username", flex: 1 },
    { field: "email", headerName: "Email", flex: 1.4 },
    {
      field: "is_active",
      headerName: "Status",
      flex: 0.8,
      renderCell: (params) =>
        params.row.is_active ? (
          <span style={{ color: "green", fontWeight: 500 }}>✅ Active</span>
        ) : (
          <span style={{ color: "red", fontWeight: 500 }}>⛔ Inactive</span>
        ),
    },
  ];

  return (
    <UnifiedPageWrapper
      title="Manager Group"
      actionButton={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedManager({
              username: "",
              email: "",
              is_active: true,
            });
            setOpenEdit(true);
          }}
          sx={{ borderRadius: "8px", textTransform: "none" }}
        >
          Add Manager
        </Button>
      }
    >
      <SmartTable
        rows={managers}
        columns={columns}
        onRowClick={handleRowClick}
        key={JSON.stringify(managers.map((m) => `${m.id}-${m.is_active}`))}
      />

      {/* ✳️ Modal */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedManager?.id ? "Edit Manager" : "New Manager"}
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Username"
              value={selectedManager?.username || ""}
              onChange={(e) =>
                setSelectedManager({ ...selectedManager, username: e.target.value })
              }
              fullWidth
            />
            <TextField
              label="Email"
              value={selectedManager?.email || ""}
              onChange={(e) =>
                setSelectedManager({ ...selectedManager, email: e.target.value })
              }
              fullWidth
            />
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography>Active</Typography>
              <Switch
                checked={selectedManager?.is_active || false}
                onChange={(e) =>
                  setSelectedManager({
                    ...selectedManager,
                    is_active: e.target.checked,
                  })
                }
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          {selectedManager?.id && (
            <Button color="error" onClick={() => handleDeleteManager(selectedManager.id)}>
              Delete
            </Button>
          )}
          <Box flexGrow={1} />
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveManager}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </UnifiedPageWrapper>
  );
}
